import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sessionAPI, gamificationAPI, teachingAPI, curriculumAPI } from '../services/api';
import QuestionPanel from '../components/session/QuestionPanel';
import ContentPanel from '../components/session/ContentPanel';
import DebriefPanel from '../components/session/DebriefPanel';
import XpBar from '../components/gamification/XpBar';
import BadgeToast from '../components/gamification/BadgeToast';
import CognitiveStateIndicator from '../components/analytics/CognitiveStateIndicator';
import AudioOverlay from '../components/audio/AudioOverlay';
import RenderRouter from '../game/RenderRouter';
import EventBus from '../game/EventBus';
import { useViewportOrientation } from '../context/ViewportOrientationContext';

const scenarioOsApiBase = (process.env.REACT_APP_SCENARIO_OS_API || '/scenario-os').replace(/\/$/, '');

function extractCue(cue) {
  if (!cue) return { text: null, audioUrl: null };
  if (typeof cue === 'string') return { text: cue, audioUrl: null };
  if (typeof cue === 'object') {
    const text = cue.text || cue.script || null;
    const audioUrl = cue.audio_url || cue.audioUrl || null;
    if (!text && !audioUrl) return { text: null, audioUrl: null };
    return { text, audioUrl };
  }
  return { text: String(cue), audioUrl: null };
}

// Field-orientation pill that flips the learner between the legacy
// landscape broadcast view and the coach playbook portrait view.
// Persistence is owned by ViewportOrientationProvider — we only
// emit the toggle. Hidden when the persisted/default orientation
// already matches the only sensible value for this shell (currently
// always shown for learner-app, where the learner can choose either).
function OrientationToggleButton() {
  const { orientation, toggleOrientation, isOverridden, defaultOrientation, resetOrientation } = useViewportOrientation();
  const isPortrait = orientation === 'portrait';
  const onPrimary = () => toggleOrientation();
  const onSecondary = (e) => {
    if (e.shiftKey) {
      // Power-user affordance: shift+click resets to the shell default
      // (handy if a learner gets stuck in an orientation that doesn't
      // fit their device and wants to clear the override quickly).
      resetOrientation();
    }
  };
  return (
    <button
      type="button"
      className={`btn-small camera-btn ${isPortrait ? 'active' : ''}`}
      onClick={onPrimary}
      onMouseDown={onSecondary}
      title={
        `Field orientation: ${isPortrait ? 'portrait playbook' : 'landscape broadcast'}.`
        + ` Click to flip${isOverridden ? ` (currently overriding the ${defaultOrientation} default — Shift+click to reset)` : ''}.`
      }
      data-testid="orientation-toggle"
      aria-pressed={isPortrait}
    >
      {isPortrait ? '⇆ Landscape' : '⇅ Portrait'}
    </button>
  );
}

export default function SessionPage() {
  const { domain, scenarioId } = useParams();
  const [searchParams] = useSearchParams();
  // The URL `?mode=` param is honored only as an initial hint (used by
  // some coach launchers that already know the scene type).  Once the
  // session/start response comes back we always trust `data.mode` —
  // that is the resolved scenario mode (simulation / interactive /
  // quiz) and is the only source the UI should branch on for rendering
  // or for picking advance()/nextContent() endpoints.  Previously this
  // defaulted to "simulation" which made AI-domain interactive scenes
  // try to call /advance and crash with "Session is not in SIMULATION
  // mode" on the Next button.
  const initialMode = searchParams.get('mode');
  // ``?from=coach`` is appended by every coach-side launcher (Try as learner,
  // ScenePreview "Try as learner", etc.) so the post-completion debrief can
  // return the coach to the Library / Designer instead of the learner-side
  // domain page (which 404s for coach-only accounts and is the wrong context
  // for an authoring session).
  const fromCoach = searchParams.get('from') === 'coach';
  const { t } = useTranslation('session');
  const tc = (key) => t(key, { ns: 'common' });
  const navigate = useNavigate();

  const [sessionId, setSessionId] = useState(null);
  const [sessionMode, setSessionMode] = useState(initialMode || null);
  const [phase, setPhase] = useState('starting');
  const [frame, setFrame] = useState(null);
  const [actors, setActors] = useState([]);
  const [sceneManifest, setSceneManifest] = useState(null);
  const [rendererConfig, setRendererConfig] = useState(null);
  const [question, setQuestion] = useState(null);
  const [content, setContent] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [debrief, setDebrief] = useState(null);
  const [progress, setProgress] = useState(0);
  // Default to QB perspective. The football_scene_manifest_3d.yaml only
  // exposes qb_perspective (default) and top_down, and starting in top_down
  // briefly before swapping to QB caused a confusing "wrong camera" flash
  // for coaches reviewing scenes. Initializing to qb_perspective means the
  // first paint matches the manifest default.
  const [cameraMode, setCameraMode] = useState('qb_perspective');
  const [advancing, setAdvancing] = useState(false);
  const [error, setError] = useState(null);
  const [cognitiveState, setCognitiveState] = useState(null);
  const [adaptation, setAdaptation] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [newBadges, setNewBadges] = useState([]);
  const [xpEarned, setXpEarned] = useState(0);
  const [coachAudioUrl, setCoachAudioUrl] = useState(null);
  const [coachingCue, setCoachingCue] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [sessionPhase, setSessionPhase] = useState('starting');
  const [teachingSegment, setTeachingSegment] = useState(null);
  const [teachingProgress, setTeachingProgress] = useState(0);
  const [totalSegments, setTotalSegments] = useState(0);
  const [paused, setPaused] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  // Playback speed multiplier — applied both to the 3D tween (via the
  // `set-playback-speed` event consumed by FootballScene3D) and to the
  // <audio> element's `playbackRate` so coach narration tracks the
  // visuals when stepping through plays slowly.
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  // GoArmy-Edge-style "More views" disclosure. Primary buttons (QB,
  // Top Down) stay always-visible; secondary cameras (sideline,
  // broadcast, end zone, follow ball/player) live behind this toggle.
  const [showAllCameras, setShowAllCameras] = useState(false);
  const [followActorId, setFollowActorId] = useState(null);
  // Four user-saved camera slots persisted across reloads.
  // Stored as { camera_id, follow_actor_id? } so a saved
  // "Follow Player: WR1" actually recalls the right target.
  const [savedCameras, setSavedCameras] = useState(() => {
    try {
      const raw = localStorage.getItem('scenario_os_saved_cameras');
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length === 4) return parsed;
    } catch (_) { /* ignore */ }
    return [null, null, null, null];
  });

  const hasAuthoredCueRef = React.useRef(false);
  const sessionEndRecordedRef = useRef(false);
  const sessionIdRef = useRef(null);
  const phaseRef = useRef('starting');
  const mountGenerationRef = useRef(0);

  sessionIdRef.current = sessionId;
  phaseRef.current = phase;

  /** Idempotent server-side end + mastery; safe to call from multiple UI paths. */
  const markSessionEnded = useCallback(() => {
    sessionEndRecordedRef.current = true;
  }, []);

  useEffect(() => {
    const gen = ++mountGenerationRef.current;
    return () => {
      const saved = gen;
      queueMicrotask(() => {
        if (mountGenerationRef.current !== saved) return;
        const sid = sessionIdRef.current;
        const ph = phaseRef.current;
        if (!sid || sessionEndRecordedRef.current) return;
        if (ph !== 'active') return;
        const token = localStorage.getItem('scenario_os_token');
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        fetch(`${scenarioOsApiBase}/session/${sid}/end`, {
          method: 'POST',
          headers,
          keepalive: true,
        }).catch(() => {});
      });
    };
  }, []);

  const pollAgentStatus = useCallback(async (sid) => {
    setAgentLoading(true);
    const maxAttempts = 60;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const { data } = await sessionAPI.getAgentStatus(sid);
        if (data.agent_ready) {
          if (data.coaching_cue && !hasAuthoredCueRef.current) {
            const { text, audioUrl } = extractCue(data.coaching_cue);
            setCoachingCue(text);
            if (audioUrl) setCoachAudioUrl(audioUrl);
          }
          setAgentLoading(false);
          return;
        }
      } catch {}
      await new Promise(r => setTimeout(r, 3000));
    }
    setAgentLoading(false);
  }, []);

  const startSession = useCallback(async () => {
    try {
      sessionEndRecordedRef.current = false;
      const { data } = await sessionAPI.start({ domain, scenario_id: scenarioId });
      setSessionId(data.session_id);
      // Trust the API's resolved mode for every subsequent rendering
      // and routing decision (see initialMode comment at the top).
      if (data.mode) setSessionMode(data.mode);
      if (data.actors) setActors(data.actors);
      if (data.scene_manifest) {
        setSceneManifest(data.scene_manifest);
        const defaultCam = (data.scene_manifest.camera_options || [])
          .find(c => c.is_default)?.camera_id || 'qb_perspective';
        setCameraMode(defaultCam);
        setTimeout(() => EventBus.emit('switch-camera', defaultCam), 500);
      }
      if (data.renderer_config) setRendererConfig(data.renderer_config);
      // Audio "playing twice with different voices" fix: ALWAYS resolve
      // a single canonical audio URL per turn through `pickAudio()`
      // (frame.audio_url > coaching_cue.audio_url) and ALWAYS set
      // coachAudioUrl + coachingCue exactly once per render. Previously
      // startSession's simulation branch double-set the URL — once from
      // the start payload's coaching_cue (Voice A) and once from the
      // adv.frame.audio_url that came back milliseconds later (Voice B).
      // Both setStates ran in sequence; the AudioOverlay autoplay effect
      // then fired twice, sometimes leading the second audio's
      // play().catch() handler to fall back to speechSynthesis while
      // the first audio was still loading — the audible result was two
      // overlapping voices.
      if (data.mode === 'simulation') {
        // For simulation mode, defer ALL audio decisions until we have
        // the first frame from advance(). That is the canonical event
        // payload; the start payload's coaching_cue is just a header.
        setPhase('active');
        pollAgentStatus(data.session_id);
        try {
          const { data: gp } = await gamificationAPI.getProfile(domain);
          if (gp) setGamification({ profile: gp });
        } catch {}
        const adv = await sessionAPI.advance(data.session_id);
        setFrame(adv.data.frame);
        if (adv.data.frame?.annotations?.length) {
          EventBus.emit('set-annotations', adv.data.frame.annotations);
        }
        if (adv.data.coaching_cue || adv.data.frame?.audio_url) {
          const { text, audioUrl: cueUrl } = extractCue(adv.data.coaching_cue);
          const finalUrl = adv.data.frame?.audio_url || cueUrl || null;
          setCoachingCue(text);
          setCoachAudioUrl(finalUrl);
          if (adv.data.frame?.coaching_script) hasAuthoredCueRef.current = true;
        }
        if (adv.data.question) setQuestion(adv.data.question);
      } else {
        // Learning-content mode — no advance() to defer to. The start
        // payload's coaching_cue IS the canonical narration for the
        // first content block. Set audio + text exactly once here,
        // and mark hasAuthoredCueRef so the subsequent pollAgentStatus
        // doesn't reset and re-play it (which used to cause the cue
        // audio to fire twice for AI-domain interactive scenes).
        if (data.coaching_cue) {
          const { text, audioUrl } = extractCue(data.coaching_cue);
          setCoachingCue(text);
          setCoachAudioUrl(audioUrl || null);
          hasAuthoredCueRef.current = true;
        }
        setPhase('active');
        pollAgentStatus(data.session_id);
        try {
          const { data: gp } = await gamificationAPI.getProfile(domain);
          if (gp) setGamification({ profile: gp });
        } catch {}
        const nc = await sessionAPI.nextContent(data.session_id);
        setContent(nc.data.content_block || null);
        setQuestion(nc.data.question || null);
        setProgress(nc.data.progress || 0);
        if (nc.data.is_final) setPhase('complete');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start session');
    }
  }, [domain, scenarioId, pollAgentStatus, markSessionEnded]);

  React.useEffect(() => { startSession(); }, [startSession]);

  useEffect(() => {
    if (!coachingCue) return;
    const dismissMs = Math.max(8000, Math.min(30000, coachingCue.length * 80));
    const timer = setTimeout(() => setCoachingCue(null), dismissMs);
    return () => clearTimeout(timer);
  }, [coachingCue]);

  useEffect(() => {
    const onClockExpired = () => {
      if (phase === 'active' && !question && !feedback && sessionId && !audioPlaying) {
        handleAdvance();
      }
    };
    EventBus.on('clock-expired', onClockExpired);
    return () => EventBus.off('clock-expired', onClockExpired);
  });

  const handleAdvance = async () => {
    if (!sessionId || advancing) return;
    setAdvancing(true);
    try {
      const { data } = await sessionAPI.advance(sessionId);
      setFrame(data.frame);
      setFeedback(null);
      if (data.frame?.annotations?.length) {
        EventBus.emit('set-annotations', data.frame.annotations);
      }
      if (data.coaching_cue || data.frame?.audio_url) {
        const { text, audioUrl } = extractCue(data.coaching_cue);
        setCoachingCue(text);
        setCoachAudioUrl(data.frame?.audio_url || audioUrl || null);
        if (data.frame?.coaching_script) hasAuthoredCueRef.current = true;
      } else {
        setCoachingCue(null);
        setCoachAudioUrl(null);
      }
      if (data.question) {
        EventBus.emit('stop-clock');
        setCoachAudioUrl(null);
        setQuestion(data.question);
      } else {
        setQuestion(null);
      }
      if (data.simulation_complete) {
        EventBus.emit('stop-clock');
        const endRes = await sessionAPI.end(sessionId);
        markSessionEnded();
        if (endRes.data.gamification) {
          setGamification(endRes.data.gamification);
          setXpEarned(endRes.data.gamification.xp_earned || 0);
          if (endRes.data.gamification.new_badges?.length) {
            setNewBadges(endRes.data.gamification.new_badges);
          }
        }
        const { data: d } = await sessionAPI.getDebrief(sessionId);
        setDebrief(d);
        setPhase('debrief');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error advancing');
    } finally {
      setAdvancing(false);
    }
  };

  const handleNextContent = async () => {
    if (!sessionId) return;
    try {
      const { data } = await sessionAPI.nextContent(sessionId);
      setContent(data.content_block || null);
      setQuestion(data.question || null);
      setProgress(data.progress || 0);
      setFeedback(null);
      if (data.is_final) {
        const endRes = await sessionAPI.end(sessionId);
        markSessionEnded();
        if (endRes.data.gamification) {
          setGamification(endRes.data.gamification);
          setXpEarned(endRes.data.gamification.xp_earned || 0);
          if (endRes.data.gamification.new_badges?.length) {
            setNewBadges(endRes.data.gamification.new_badges);
          }
        }
        const { data: d } = await sessionAPI.getDebrief(sessionId);
        setDebrief(d);
        setPhase('debrief');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading content');
    }
  };

  const handleNextTeachingSegment = async () => {
    if (!sessionId) return;
    try {
      const { data } = await teachingAPI.nextSegment(sessionId);
      if (data.teaching_complete) {
        setSessionPhase('drill');
        setTeachingSegment(null);
        if (data.linked_drill_scenario_id) {
          const adv = await sessionAPI.advance(sessionId);
          setFrame(adv.data.frame);
          if (adv.data.frame?.annotations?.length) {
            EventBus.emit('set-annotations', adv.data.frame.annotations);
          }
          if (adv.data.coaching_cue) {
            const { text: cText, audioUrl: cUrl } = extractCue(adv.data.coaching_cue);
            setCoachingCue(cText);
            if (cUrl) setCoachAudioUrl(cUrl);
          }
          if (adv.data.question) setQuestion(adv.data.question);
        }
      } else {
        setTeachingSegment(data.current_segment);
        setTeachingProgress(data.segment_index / data.total_segments);
        if (data.current_segment?.audio_url) {
          setCoachAudioUrl(data.current_segment.audio_url);
        } else if (data.current_segment?.script) {
          setCoachingCue(data.current_segment.script);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error advancing teaching');
    }
  };

  const handlePause = async () => {
    if (!sessionId) return;
    try {
      await sessionAPI.pause(sessionId);
      setPaused(true);
      EventBus.emit('stop-clock');
    } catch {}
  };

  const handleResume = async () => {
    if (!sessionId) return;
    try {
      await sessionAPI.resume(sessionId);
      setPaused(false);
      EventBus.emit('start-clock');
    } catch {}
  };

  // Where to return to once the coach/learner is "done" with this scene.
  // Coaches launch from the Library / Designer (?from=coach), so dropping
  // them on /domain/<x> hides the scene grid behind a learner-only home
  // page — and worse, that route 404s for coach-only accounts. Learners
  // continue to land on the domain page as before.
  const exitToOwner = useCallback(() => {
    if (fromCoach) {
      navigate(`/coach/scene-library/${domain}`, { replace: true });
    } else {
      navigate(`/domain/${domain}`, { replace: true });
    }
  }, [fromCoach, navigate, domain]);

  const handleNextModule = async () => {
    // Coaches don't have a "next module" — they want to return to the
    // Library / Designer where they were authoring. Skip the curriculum
    // recommendation entirely for coach-launched sessions.
    if (fromCoach) {
      exitToOwner();
      return;
    }
    try {
      const { data } = await curriculumAPI.recommend(domain);
      const rec = data?.recommendation;
      const sid = rec && ((rec.scenario_ids && rec.scenario_ids[0]) || rec.module_id);
      if (sid) {
        navigate(`/session/${domain}/${sid}`, {
          replace: true,
          state: {
            module_id: rec.module_id,
            scenario_id: (rec.scenario_ids && rec.scenario_ids[0]) || null,
            track_id: rec.track_id,
          },
        });
      } else {
        navigate(`/domain/${domain}`, { replace: true });
      }
    } catch {
      navigate(`/domain/${domain}`, { replace: true });
    }
  };

  const handleStop = async () => {
    if (!sessionId) return;
    try {
      const endRes = await sessionAPI.end(sessionId);
      markSessionEnded();
      if (endRes.data.gamification) {
        setGamification(endRes.data.gamification);
        setXpEarned(endRes.data.gamification.xp_earned || 0);
        if (endRes.data.gamification.new_badges?.length) {
          setNewBadges(endRes.data.gamification.new_badges);
        }
      }
      const { data: d } = await sessionAPI.getDebrief(sessionId);
      setDebrief(d);
      setPhase('debrief');
    } catch {
      navigate(-1);
    }
  };

  const handleAnswer = async (response) => {
    if (!sessionId) return;
    try {
      const { data } = await sessionAPI.respond(sessionId, response);
      setFeedback(data);
      setQuestion(null);
      if (data.cognitive_state) setCognitiveState(data.cognitive_state);
      if (data.adaptation) setAdaptation(data.adaptation);
      if (!data.is_correct && data.coaching_cue) {
        const { text, audioUrl } = extractCue(data.coaching_cue);
        const reviewPrefix = "That was incorrect. Let's review — ";
        if (text) setCoachingCue(reviewPrefix + text);
        if (audioUrl) setCoachAudioUrl(audioUrl);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error submitting response');
    }
  };

  if (error) {
    return (
      <div className="session-page error-page">
        <p>{tc('error')}: {error}</p>
        <button className="btn-primary" onClick={() => navigate(-1)}>{tc('back')}</button>
      </div>
    );
  }

  if (phase === 'starting') {
    return <div className="session-page"><p>{tc('loading')}</p></div>;
  }

  if (phase === 'debrief') {
    return (
      <div className="session-page">
        <DebriefPanel
          debrief={debrief}
          gamification={gamification}
          onBackToTracks={exitToOwner}
          onPlayAgain={() => {
            setPhase('starting');
            setDebrief(null);
            setActors([]);
            setSceneManifest(null);
            setRendererConfig(null);
            setCameraMode('qb_perspective');
            startSession();
          }}
          onNextModule={handleNextModule}
        />
      </div>
    );
  }

  // Derive isSimulation from the resolved session mode (API truth)
  // not the URL.  See initialMode comment at top.  Until the start
  // response lands `sessionMode` may be null; treat that as
  // "not yet known" → render non-simulation layout, which is the
  // safer default for AI-domain scenes (the simulation layout
  // expects a renderer + actors that interactive scenes lack).
  const isSimulation = sessionMode === 'simulation';
  // Phase 12 — interactive_quiz scenes with a spatial quiz_format
  // (static_spotlight / animated_board) need the 3D renderer
  // mounted exactly the same way phaser_2d / three_3d do. simple_qna
  // intentionally falls through to the QuestionPanel path — no
  // renderer needed.
  const quizFormat = (
    sceneManifest?.domain_metadata?.quiz_format
    || sceneManifest?.quiz_format
    || null
  );
  const usesRenderer = (
    ['phaser_2d', 'three_3d'].includes(sceneManifest?.scene_type)
    || (
      sceneManifest?.scene_type === 'interactive_quiz'
      && (quizFormat === 'static_spotlight' || quizFormat === 'animated_board')
    )
  );

  const cameraOptions = sceneManifest?.camera_options || [];
  const primaryCameraOptions = cameraOptions.filter((c) => c.is_primary !== false);
  const secondaryCameraOptions = cameraOptions.filter((c) => c.is_primary === false);
  const activeCameraOption = cameraOptions.find((c) => c.camera_id === cameraMode);
  const cameraNeedsTarget = !!activeCameraOption?.config?.requires_target;

  // Actors a coach might want to chase with `follow_player`. Default
  // to all "human" actors; ball/marker actors can opt out via
  // metadata.followable === false in the future.
  const followableActors = (actors || []).filter((a) => a?.actor_id);

  const handleCameraSelect = (cameraId, opts = {}) => {
    if (!cameraId) return;
    if (cameraId !== cameraMode) {
      setCameraMode(cameraId);
      EventBus.emit('switch-camera', cameraId);
    }
    if (opts.followActorId !== undefined) {
      setFollowActorId(opts.followActorId);
      EventBus.emit('set-follow-target', { actor_id: opts.followActorId });
    } else if (cameraId === 'follow_player' && !followActorId && followableActors.length) {
      const fallback = followableActors[0].actor_id;
      setFollowActorId(fallback);
      EventBus.emit('set-follow-target', { actor_id: fallback });
    }
  };

  const handleCameraCycle = () => {
    if (!cameraOptions.length) return;
    const ids = cameraOptions.map((c) => c.camera_id);
    const idx = ids.indexOf(cameraMode);
    const next = ids[(idx + 1) % ids.length];
    handleCameraSelect(next);
  };

  const handleFollowActorChange = (actorId) => {
    setFollowActorId(actorId);
    EventBus.emit('set-follow-target', { actor_id: actorId });
  };

  const handlePlaybackSpeed = (speed) => {
    const next = Number(speed) || 1.0;
    setPlaybackSpeed(next);
    EventBus.emit('set-playback-speed', next);
  };

  const handleSaveCameraSlot = (slotIdx) => {
    const next = [...savedCameras];
    next[slotIdx] = {
      camera_id: cameraMode,
      follow_actor_id: cameraMode === 'follow_player' ? followActorId : null,
      label: activeCameraOption?.label || cameraMode,
    };
    setSavedCameras(next);
    try {
      localStorage.setItem('scenario_os_saved_cameras', JSON.stringify(next));
    } catch (_) { /* quota / private mode — ignore */ }
  };

  const handleRecallCameraSlot = (slotIdx) => {
    const slot = savedCameras[slotIdx];
    if (!slot?.camera_id) return;
    handleCameraSelect(slot.camera_id, { followActorId: slot.follow_actor_id ?? undefined });
  };

  return (
    <div className="session-page">
      <div className="session-hud">
        <div className="session-controls" data-testid="session-controls">
          {!paused ? (
            <button className="btn-small session-ctrl-btn" onClick={handlePause} title="Pause training">
              ⏸ Pause
            </button>
          ) : (
            <button className="btn-small session-ctrl-btn session-ctrl-resume" onClick={handleResume} title="Resume training">
              ▶ Resume
            </button>
          )}
          <button className="btn-small session-ctrl-btn session-ctrl-stop" onClick={handleStop} title="Stop and view results">
            ⏹ Stop
          </button>
        </div>

        {agentLoading && (
          <div className="agent-loading-indicator" data-testid="agent-loading">
            <span className="agent-pulse"></span>
            Coaches connecting...
          </div>
        )}
        {gamification?.profile && (
          <XpBar
            xpCurrent={(gamification.profile.xp_total || 0) % 500}
            xpToNext={500}
            level={gamification.profile.level || 1}
            levelName={gamification.profile.level_title || ''}
            xpEarned={xpEarned}
          />
        )}
        {cognitiveState && (
          <CognitiveStateIndicator
            state={cognitiveState.state}
            confidence={cognitiveState.confidence}
            trend={cognitiveState.trend}
            adaptation={adaptation}
          />
        )}
      </div>

      <BadgeToast badges={newBadges} onDismiss={() => setNewBadges([])} />

      {(coachAudioUrl || coachingCue) && (
        <AudioOverlay
          audioUrl={coachAudioUrl}
          fallbackText={coachingCue || undefined}
          autoPlay={!paused}
          playbackRate={playbackSpeed}
          onEnded={() => { setCoachAudioUrl(null); setCoachingCue(null); setAudioPlaying(false); }}
          onPlay={() => setAudioPlaying(true)}
        />
      )}

      {sessionPhase === 'film_room' && teachingSegment && (
        <div className="film-room-container">
          <div className="film-room-header">
            <h2>QB Room</h2>
            <div className="teaching-progress-bar">
              <div className="teaching-progress-fill" style={{ width: `${teachingProgress * 100}%` }} />
            </div>
          </div>

          <div className="renderer-container">
            {teachingSegment.scene_directive && (
              <RenderRouter
                sceneManifest={sceneManifest}
                rendererConfig={rendererConfig}
                actors={actors}
                frame={frame}
              />
            )}
          </div>

          {teachingSegment.type === 'narration' && (
            <div className="narration-panel" data-testid="narration-panel">
              <div className="narration-icon">🎙️</div>
              <p className="narration-script">{teachingSegment.script}</p>
            </div>
          )}

          {teachingSegment.type === 'question' && teachingSegment.question_template_id && (
            <div className="question-sidebar">
              <QuestionPanel question={question} onAnswer={handleAnswer} timeLimit={question?.time_limit_ms} />
            </div>
          )}

          <button className="btn-primary film-room-next" onClick={handleNextTeachingSegment}>
            {tc('next')}
          </button>
        </div>
      )}

      {isSimulation && (
        <div className={`session-split-layout ${question && !feedback ? 'has-question' : ''}`}>
          <div className="session-field-area">
            <div className="renderer-container">
              <RenderRouter
                sceneManifest={sceneManifest}
                rendererConfig={rendererConfig}
                actors={actors}
                frame={frame}
                scene={sceneManifest}
                feedback={feedback}
              >
                <ContentPanel contentBlock={content} progress={progress} onNext={handleNextContent} />
              </RenderRouter>

              {usesRenderer && cameraOptions.length > 0 && (
                <div className="camera-controls" data-testid="camera-controls">
                  {primaryCameraOptions.map((opt) => (
                    <button
                      key={opt.camera_id}
                      className={`btn-small camera-btn ${cameraMode === opt.camera_id ? 'active' : ''}`}
                      onClick={() => handleCameraSelect(opt.camera_id)}
                      data-testid={`camera-btn-${opt.camera_id}`}
                    >
                      {opt.label || opt.camera_id}
                    </button>
                  ))}

                  {secondaryCameraOptions.length > 0 && (
                    <>
                      <button
                        className={`btn-small camera-btn ${showAllCameras ? 'active' : ''}`}
                        onClick={() => setShowAllCameras((v) => !v)}
                        title="More views"
                        data-testid="camera-more-toggle"
                      >
                        {showAllCameras ? '▾ Views' : '▸ More views'}
                      </button>
                      {showAllCameras && (
                        <select
                          className="camera-select"
                          value={cameraMode}
                          onChange={(e) => handleCameraSelect(e.target.value)}
                          data-testid="camera-select"
                        >
                          {cameraOptions.map((opt) => (
                            <option key={opt.camera_id} value={opt.camera_id}>
                              {opt.label || opt.camera_id}
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  )}

                  {cameraNeedsTarget && followableActors.length > 0 && (
                    <select
                      className="camera-select"
                      value={followActorId || ''}
                      onChange={(e) => handleFollowActorChange(e.target.value)}
                      title="Follow this player"
                      data-testid="camera-follow-target"
                    >
                      <option value="">Pick player…</option>
                      {followableActors.map((a) => (
                        <option key={a.actor_id} value={a.actor_id}>
                          {a.label || a.role || a.actor_id}
                        </option>
                      ))}
                    </select>
                  )}

                  <select
                    className="camera-select"
                    value={String(playbackSpeed)}
                    onChange={(e) => handlePlaybackSpeed(parseFloat(e.target.value))}
                    title="Playback speed"
                    data-testid="playback-speed"
                  >
                    <option value="0.5">0.5×</option>
                    <option value="0.75">0.75×</option>
                    <option value="1">1×</option>
                    <option value="1.5">1.5×</option>
                    <option value="2">2×</option>
                  </select>

                  <button
                    className="btn-small camera-btn"
                    onClick={handleCameraCycle}
                    title="Cycle camera"
                    data-testid="camera-cycle-btn"
                  >
                    ⟳
                  </button>

                  {/* Field-orientation toggle. Defaults to landscape
                      (broadcast view) under the learner shell; learners
                      on a phone holding it portrait can flip to the
                      coach playbook layout. The setting persists in
                      localStorage via ViewportOrientationProvider so
                      it sticks across sessions / refreshes. */}
                  <OrientationToggleButton />

                  <div className="camera-saved-slots" data-testid="camera-saved-slots" title="Saved camera slots — click to recall, hold Shift+click to save current">
                    {[0, 1, 2, 3].map((idx) => {
                      const slot = savedCameras[idx];
                      const filled = !!slot?.camera_id;
                      return (
                        <button
                          key={idx}
                          className={`btn-small camera-slot ${filled ? 'filled' : 'empty'}`}
                          onClick={(e) => (e.shiftKey ? handleSaveCameraSlot(idx) : handleRecallCameraSlot(idx))}
                          title={filled ? `${slot.label}${slot.follow_actor_id ? ` (${slot.follow_actor_id})` : ''} — click to recall, Shift+click to overwrite` : `Empty slot — Shift+click to save current`}
                          data-testid={`camera-slot-${idx}`}
                        >
                          {filled ? `★${idx + 1}` : `☆${idx + 1}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {coachingCue && (
              <div className="coaching-cue-inline" data-testid="coaching-cue">
                <div className="coaching-cue-icon">💬</div>
                <p className="coaching-cue-text">{coachingCue}</p>
                <button className="coaching-cue-dismiss" onClick={() => setCoachingCue(null)}>×</button>
              </div>
            )}
          </div>

          {!feedback && question && (
            <div className="question-sidebar" data-testid="question-sidebar">
              <QuestionPanel question={question} onAnswer={handleAnswer} timeLimit={question.time_limit_ms} />
            </div>
          )}
        </div>
      )}

      {feedback && (
        <div className={`feedback-banner ${feedback.is_correct ? 'correct' : 'incorrect'}`}>
          <strong>{feedback.is_correct ? tc('correct') : tc('incorrect')}</strong>
          {!feedback.is_correct && <em className="feedback-review-hint">Let's review the play again.</em>}
          <span>{feedback.feedback}</span>
          <button className="btn-small" onClick={() => {
            setFeedback(null);
            setCoachingCue(null);
            setCoachAudioUrl(null);
            EventBus.emit('start-clock');
            if (isSimulation) handleAdvance();
            else handleNextContent();
          }}>
            {tc('continue')}
          </button>
        </div>
      )}

      {!isSimulation && !feedback && question && (
        <div className="question-panel-standalone">
          <QuestionPanel question={question} onAnswer={handleAnswer} timeLimit={question.time_limit_ms} />
        </div>
      )}

      {!isSimulation && !feedback && !question && content && (
        <ContentPanel contentBlock={content} progress={progress} onNext={handleNextContent} />
      )}

      {!feedback && !question && !content && isSimulation && !advancing && !paused && (
        <button className="btn-primary session-next-btn" onClick={handleAdvance}>
          {tc('next')}
        </button>
      )}

      {paused && (
        <div className="session-paused-overlay">
          <div className="session-paused-message">
            <h2>Training Paused</h2>
            <p>Click Resume to continue or Stop to end the session.</p>
          </div>
        </div>
      )}
    </div>
  );
}
